import React from 'react';
import { School, Users, GraduationCap, Heart, BookOpen, Activity, TrendingUp, AlertTriangle, AlertOctagon, XCircle, Eye, Info, CheckCircle2, Clock, Server, RefreshCw, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { QualityRing, ProgressBar } from './dashboard/DashboardCharts';
import { containerVariants, itemVariants } from '../shared/animationVariants';
import { useDashboardData } from './dashboard/useDashboardData';
export function DashboardPage({
  onNavigate
}: {
  onNavigate?: (view: string) => void;
}) {
  const {
    stats,
    contentStats,
    pipelineLoading,
    strictModeOn,
    handleRefreshPipeline,
    passCount,
    warnCount,
    failCount,
    avgScore,
    totalValidated,
    severityCounts,
    systemHealth,
    recentActivity
  } = useDashboardData();
  return <PageLayout title="Dashboard" subtitle="Overzicht van het Meester djay platform" actions={<ConnectionBadge status="live" data-id="element-6644" />} data-id="element-6643">
      {/* Section 1: Platform Overview */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-6645">
        <motion.div variants={itemVariants} data-id="element-6646">
          <StatCard label="Leerlingen" value={stats.totalLearners} icon={<Users className="w-4 h-4 text-blue-500" data-id="element-6648" />} data-id="element-6647" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6649">
          <StatCard label="Docenten" value={stats.totalTeachers} icon={<GraduationCap className="w-4 h-4 text-indigo-500" data-id="element-6651" />} data-id="element-6650" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6652">
          <StatCard label="Ouders" value={stats.totalParents} icon={<Heart className="w-4 h-4 text-rose-500" data-id="element-6654" />} data-id="element-6653" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-6655">
          <StatCard label="Scholen" value={stats.totalSchools} icon={<School className="w-4 h-4 text-purple-500" data-id="element-6657" />} data-id="element-6656" />
        </motion.div>
      </motion.div>

      {/* Section 2: Content & Quality */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-6658">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-6659">
          Content & Kwaliteit
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-6660">
          {/* Left: Content Stats */}
          <div className="space-y-4" data-id="element-6661">
            <div className="flex items-center justify-between" data-id="element-6662">
              <div className="flex items-center gap-2" data-id="element-6663">
                <BookOpen className="w-4 h-4 text-blue-500" data-id="element-6664" />
                <span className="text-sm font-medium text-gray-700" data-id="element-6665">
                  Vragenbank
                </span>
              </div>
              <span className="text-lg font-bold text-gray-900" data-id="element-6666">
                {contentStats.totalQuestions.toLocaleString('nl')}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-6667">
              <div className="flex items-center gap-2" data-id="element-6668">
                <Tag className="w-4 h-4 text-purple-500" data-id="element-6669" />
                <span className="text-sm font-medium text-gray-700" data-id="element-6670">
                  Families
                </span>
              </div>
              <span className="text-sm text-gray-600" data-id="element-6671">
                {contentStats.totalFamilies} / {contentStats.taxonomyFamilies}
              </span>
            </div>
            <ProgressBar value={contentStats.coveragePct} color={contentStats.coveragePct >= 80 ? 'bg-emerald-500' : contentStats.coveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label="Seed dekking" data-id="element-6672" />
            <ProgressBar value={contentStats.visualCoveragePct} color={contentStats.visualCoveragePct >= 80 ? 'bg-emerald-500' : contentStats.visualCoveragePct >= 50 ? 'bg-amber-500' : 'bg-red-500'} label={`Visuele componenten (${contentStats.usedVisualsCount}/${contentStats.registeredVisuals})`} data-id="element-6673" />
          </div>

          {/* Right: Quality & Validation */}
          <div className="space-y-4" data-id="element-6674">
            <div className="flex items-center justify-between" data-id="element-6675">
              <div className="flex items-center gap-3" data-id="element-6676">
                {avgScore !== null ? <QualityRing score={avgScore} data-id="element-6677" /> : <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" data-id="element-6678" />}
                <div data-id="element-6679">
                  <div className="text-sm font-medium text-gray-700" data-id="element-6680">
                    Kwaliteitsscore
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-6681">
                    alle {contentStats.totalQuestions.toLocaleString('nl')}{' '}
                    vragen
                  </div>
                </div>
              </div>
              <button onClick={handleRefreshPipeline} disabled={pipelineLoading} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40" title="Validatie vernieuwen" data-id="element-6682">
                <RefreshCw className={`w-4 h-4 ${pipelineLoading ? 'animate-spin' : ''}`} data-id="element-6683" />
              </button>
            </div>

            {pipelineLoading ? <div className="flex gap-4" data-id="element-6684">
                {['Geslaagd', 'Review', 'Afgekeurd'].map(l => <div key={l} className="flex-1 bg-gray-50 rounded-lg p-2 animate-pulse" data-id="element-6685">
                    <div className="h-5 bg-gray-200 rounded mb-1" data-id="element-6686" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" data-id="element-6687" />
                  </div>)}
              </div> : passCount !== null ? <>
                <div className="flex gap-3" data-id="element-6688">
                  <div className="flex-1 bg-emerald-50 rounded-lg p-2.5 text-center" data-id="element-6689">
                    <div className="text-lg font-bold text-emerald-700" data-id="element-6690">
                      {passCount}
                    </div>
                    <div className="text-xs text-emerald-600" data-id="element-6691">Geslaagd</div>
                  </div>
                  <div className="flex-1 bg-amber-50 rounded-lg p-2.5 text-center" data-id="element-6692">
                    <div className="text-lg font-bold text-amber-700" data-id="element-6693">
                      {warnCount}
                    </div>
                    <div className="text-xs text-amber-600" data-id="element-6694">Review</div>
                  </div>
                  <div className="flex-1 bg-red-50 rounded-lg p-2.5 text-center" data-id="element-6695">
                    <div className="text-lg font-bold text-red-700" data-id="element-6696">
                      {failCount}
                    </div>
                    <div className="text-xs text-red-600" data-id="element-6697">Afgekeurd</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-6698">
                  <div className="h-full bg-emerald-500" style={{
                width: `${totalValidated > 0 ? (passCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-6699" />
                  <div className="h-full bg-amber-400" style={{
                width: `${totalValidated > 0 ? (warnCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-6700" />
                  <div className="h-full bg-red-500" style={{
                width: `${totalValidated > 0 ? (failCount ?? 0) / totalValidated * 100 : 0}%`
              }} data-id="element-6701" />
                </div>
              </> : <div className="text-sm text-gray-400 text-center py-4" data-id="element-6702">
                Validatie wordt geladen...
              </div>}
          </div>
        </div>
      </motion.div>

      {/* Section 2b — Phase 1: 5-level severity breakdown across ALL issues.
                  The 3 tiles above count QUESTIONS (verdict per question, always
                  3 outcomes). These 5 tiles count ISSUES by display-severity. */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-6" data-id="element-6703">
        <div className="flex items-center justify-between mb-3" data-id="element-6704">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6705">
            Bevindingen per ernst
          </h3>
          <span className="text-[10px] text-gray-400" data-id="element-6706">
            5-niveau model · per bevinding
          </span>
        </div>
        {severityCounts === null ? <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-6707">
            {Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="h-12 bg-gray-50 rounded-lg animate-pulse" data-id="element-6708" />)}
          </div> : <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-id="element-6709">
            {[{
          key: 'critical' as const,
          label: 'Kritiek',
          Icon: AlertOctagon,
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700',
          iconClass: 'text-red-600',
          hint: 'Blocker · ernst ≥90 (student-facing risico)'
        }, {
          key: 'blocker' as const,
          label: 'Blocker',
          Icon: XCircle,
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          text: 'text-orange-700',
          iconClass: 'text-orange-600',
          hint: 'Blocker · ernst 70-89'
        }, {
          key: 'review' as const,
          label: 'Review',
          Icon: Eye,
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-700',
          iconClass: 'text-yellow-600',
          hint: 'Coach · ernst ≥45 (twijfel, vraagt menselijk oordeel)'
        }, {
          key: 'coach' as const,
          label: 'Coach',
          Icon: AlertTriangle,
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-700',
          iconClass: 'text-blue-600',
          hint: 'Coach · ernst <45 (auteursfeedback)'
        }, {
          key: 'info' as const,
          label: 'Info',
          Icon: Info,
          bg: 'bg-slate-50',
          border: 'border-slate-200',
          text: 'text-slate-700',
          iconClass: 'text-slate-500',
          hint: 'Observatie zonder actie'
        }].map(tier => {
          const count = severityCounts[tier.key] ?? 0;
          const TierIcon = tier.Icon;
          return <div key={tier.key} className={`flex items-center gap-2 p-2.5 rounded-lg border ${tier.bg} ${tier.border}`} title={tier.hint} data-id="element-6710">
                  <TierIcon className={`w-4 h-4 flex-shrink-0 ${tier.iconClass}`} data-id="element-6711" />
                  <div className="min-w-0 flex-1" data-id="element-6712">
                    <p className={`text-lg font-bold leading-none ${tier.text}`} data-id="element-6713">
                      {count.toLocaleString('nl-NL')}
                    </p>
                    <p className={`text-[10px] font-medium ${tier.text} mt-0.5`} data-id="element-6714">
                      {tier.label}
                    </p>
                  </div>
                </div>;
        })}
          </div>}
        <p className="text-[10px] text-gray-400 mt-2 leading-relaxed" data-id="element-6715">
          <span className="font-semibold" data-id="element-6716">Verdict</span> (boven) telt vragen — 3
          uitkomsten van de gate. <span className="font-semibold" data-id="element-6717">Ernst</span>{' '}
          (hier) telt individuele bevindingen — één vraag kan meerdere
          bevindingen op verschillende niveaus hebben.
        </p>
      </motion.div>

      {/* Section 3: Activity & System Health */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6" data-id="element-6718">
        {/* Recent Activity — F-A3 (19 May 2026): timestamps in this list
            are synthetic (computed as `now - fixed offset`). Counts and
            messages ARE real, but the times are not — show a mock badge
            so admins don't read this as an audit-grade activity log.
            Audit §11 / §19. */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6719">
          <div className="flex items-center justify-between mb-4" data-id="element-6720">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2" data-id="element-6721">
              <Clock className="w-4 h-4" data-id="element-6722" /> Recente Activiteit
            </h3>
            <ConnectionBadge status="mock" label="Demo-tijden" data-id="element-6723" />
          </div>
          <div className="space-y-3" data-id="element-6724">
            {recentActivity.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-6725">
                <div className={`mt-0.5 flex-shrink-0 ${item.type === 'success' ? 'text-emerald-500' : item.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} data-id="element-6726">
                  {item.type === 'success' ? <CheckCircle2 className="w-4 h-4" data-id="element-6727" /> : item.type === 'warning' ? <AlertTriangle className="w-4 h-4" data-id="element-6728" /> : <Activity className="w-4 h-4" data-id="element-6729" />}
                </div>
                <div className="min-w-0 flex-1" data-id="element-6730">
                  <p className="text-sm text-gray-700 leading-snug" data-id="element-6731">
                    {item.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-6732">
                    {item.time}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* System Health */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6733">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-6734">
            <Server className="w-4 h-4" data-id="element-6735" /> Systeemgezondheid
          </h3>
          <div className="space-y-4" data-id="element-6736">
            <div className="flex items-center justify-between" data-id="element-6737">
              <span className="text-sm text-gray-600" data-id="element-6738">Strict Mode</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${strictModeOn ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-6739">
                {strictModeOn ? 'AAN' : 'UIT'}
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-6740">
              <span className="text-sm text-gray-600" data-id="element-6741">Gem. responstijd</span>
              <span className="text-sm font-semibold text-gray-800" data-id="element-6742">
                {systemHealth.responseTime}ms
              </span>
            </div>
            <div data-id="element-6743">
              <div className="flex items-center justify-between mb-1.5" data-id="element-6744">
                <span className="text-sm text-gray-600" data-id="element-6745">Opslag</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-6746">
                  {systemHealth.storageMb} / 5 MB
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-6747">
                <div className="h-full bg-violet-500 rounded-full" style={{
                width: `${systemHealth.storagePct}%`
              }} data-id="element-6748" />
              </div>
            </div>
            <div data-id="element-6749">
              <div className="flex items-center justify-between mb-1.5" data-id="element-6750">
                <span className="text-sm text-gray-600" data-id="element-6751">Actief deze week</span>
                <span className="text-sm font-semibold text-gray-800" data-id="element-6752">
                  {stats.activeThisWeek} leerlingen
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-6753">
                <div className="h-full bg-blue-500 rounded-full" style={{
                width: `${Math.min(100, stats.activeThisWeek / Math.max(stats.totalLearners, 1) * 100)}%`
              }} data-id="element-6754" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Section 4: Quick Insights */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-6755">
        {/* Subscriptions */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6756">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-6757">
            Abonnementen
          </h3>
          <div className="space-y-2.5" data-id="element-6758">
            {stats.planDistribution.map(item => <div key={item.label} data-id="element-6759">
                <div className="flex justify-between text-xs mb-1" data-id="element-6760">
                  <span className="text-gray-600" data-id="element-6761">{item.label}</span>
                  <span className="font-semibold text-gray-800" data-id="element-6762">
                    {item.count}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-6763">
                  <div className={`h-full rounded-full ${item.color}`} style={{
                width: `${item.pct}%`
              }} data-id="element-6764" />
                </div>
              </div>)}
          </div>
        </motion.div>

        {/* Top Learners */}
        <motion.div variants={itemVariants} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5" data-id="element-6765">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-6766">
            <TrendingUp className="w-3.5 h-3.5" data-id="element-6767" /> Top Leerlingen
          </h3>
          {stats.topLearners.length > 0 ? <div className="space-y-2.5" data-id="element-6768">
              {stats.topLearners.map((item, i) => <div key={i} className="flex items-center gap-2.5" data-id="element-6769">
                  <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0" data-id="element-6770">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-6771">
                    <p className="text-xs font-medium text-gray-800 truncate" data-id="element-6772">
                      {item.name}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-violet-600" data-id="element-6773">
                    {item.xp.toLocaleString('nl-NL')} XP
                  </span>
                </div>)}
            </div> : <p className="text-xs text-gray-400" data-id="element-6774">Geen leerlingen gevonden</p>}
        </motion.div>
      </motion.div>
    </PageLayout>;
}